"use client";

import { useAppContext } from "@/context/Context";
import { useSession } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import logo from "../../public/images/logo/logo.webp";
import logoDark from "../../public/images/light/logo/logo-dark.webp";
import avatar from "../../public/images/team/user.webp";
import Nav from "./Nav";
import UserMenu from "./UserMenu";
import DarkSwitch from "./dark-switch";
import { useEffect, useState } from "react";

/**
 * Componente HeaderDashboard que renderiza el encabezado del dashboard.
 * Muestra el logo, menú de navegación, conmutador de tema y la sección de usuario
 * (o botón de iniciar sesión si no hay sesión activa).
 *
 * @param {Object} props - Props del componente.
 * @param {string} props.display - Clase para controlar la visibilidad del botón de la barra lateral.
 * @returns {JSX.Element|null} - Encabezado del dashboard o null si el estado de sesión está cargando.
 */
const HeaderDashboard = ({ display }) => {
  const {
    rightBar,
    setRightBar,
    activeMobileMenu,
    setActiveMobileMenu,
    isLightTheme,
    toggleTheme,
  } = useAppContext();

  const { data: session, status } = useSession();
  // Estado utilizado para forzar el renderizado al detectar cambios en localStorage (logout)
  const [, setShouldRender] = useState(false);

  // Escucha los cambios en el localStorage para detectar el logout
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "logout") {
        setShouldRender((prev) => !prev);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Mientras la sesión está cargando, no se renderiza nada
  if (status === "loading") {
    return null;
  }

  return (
    <>
      {/* Conmutador de tema */}
      <DarkSwitch isLight={isLightTheme} switchTheme={toggleTheme} />

      <header className="rbt-dashboard-header rainbow-header header-default header-left-align rbt-fluid-header">
        <div className="container-fluid position-relative">
          <div className="row align-items-center justify-content-between">
            {/* Sección del logo */}
            <div className="col-lg-3 col-md-6 col-6">
              <div className="header-left d-flex">
                <div className="logo">
                  <Link href="/">
                    <Image
                      className="logo-light"
                      src={isLightTheme ? logo : logoDark}
                      width={135}
                      height={35}
                      alt="Corporate Logo"
                      priority
                    />
                  </Link>
                </div>
              </div>
            </div>

            {/* Menú principal de navegación (solo en desktop) */}
            <div className="col-lg-6 d-none d-lg-block text-center">
              <nav className="mainmenu-nav d-none d-lg-block text-center">
                <Nav />
              </nav>
            </div>

            {/* Sección de usuario o botón de iniciar sesión */}
            {session ? (
              <>
                <div className="col-lg-3 col-md-6 col-6">
                  <div className="header-right">
                    {/* Botón para el menú móvil (solo en dispositivos móviles) */}
                    <div className="mobile-menu-bar mr--10 ml--10 d-block d-lg-none">
                      <div className="hamberger">
                        <button
                          className="hamberger-button"
                          onClick={() => setActiveMobileMenu(!activeMobileMenu)}
                        >
                          <i className="feather-menu"></i>
                        </button>
                      </div>
                    </div>

                    {/* Panel de usuario con información y menú desplegable */}
                    <div className="rbt-admin-panel account-access rbt-user-wrapper right-align-dropdown">
                      <div className="rbt-admin-card grid-style">
                        <a className="d-flex align-items-center" href="#">
                          <div className="inner d-flex align-items-center">
                            <div className="img-box">
                              <Image src={avatar} alt="Admin" />
                            </div>
                            <div className="content">
                              <span className="title">
                                {session.user.email}
                              </span>
                              <p>{session.user.name}</p>
                            </div>
                          </div>
                          <div className="icon">
                            <i className="fa-sharp fa-solid fa-chevron-down"></i>
                          </div>
                        </a>
                      </div>
                      <div className="rbt-user-menu-list-wrapper">
                        <UserMenu />
                      </div>
                    </div>

                    {/* Botón para expandir/contraer la barra lateral derecha */}
                    <div className={`expand-btn-grp ${display}`}>
                      <button
                        className={`bg-solid-primary popup-dashboardright-btn session-history-collapse-section ${
                          rightBar ? "" : "collapsed"
                        }`}
                        onClick={() => setRightBar(!rightBar)}
                      >
                        <i className="fa-solid fa-arrows-turn-to-dots"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Sección para usuarios no autenticados */}
                <div className="col-lg-3 col-md-6 col-6 position-static">
                  <div className="header-right access-section">
                    <div className="header-btn">
                      <nav className="aiwave-tab">
                        <div className="tab-btn-grp nav nav-tabs d-flex flex-column flex-lg-row justify-content-center mb-3 mt-3 text-center">
                          <Link
                            href="/iniciar-sesion"
                            className="nav-link sign-in flex-fill mb-2 mb-lg-0"
                          >
                            Iniciar sesión
                          </Link>
                          <Link
                            href="/registrarse"
                            className="nav-link sign-up active flex-fill"
                          >
                            Regístrate gratis
                          </Link>
                        </div>
                      </nav>
                    </div>

                    <div className="mobile-menu-bar ml--5 d-flex justify-content-end d-lg-none">
                      <div className="hamberger">
                        <button
                          className="hamberger-button"
                          onClick={() => setActiveMobileMenu(!activeMobileMenu)}
                        >
                          <i className="fa-sharp fa-regular fa-bars"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default HeaderDashboard;
